import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
    getFullUnassignedPlanByRouteIdThunk,
    getKpiGetMarketProgressThunk,
    getMarketInfo,
    getPlanGetByMarketIdThunk,
} from "tm-shared/api";
import { backendDateFormat } from "tm-shared/constants";
import { Market, UserRole } from "tm-shared/types";

import Header from "@/components/Header";
import Meta from "@/components/Meta";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";

import { AboutPointPage } from "./AboutPointPage";
import { AboutPointPageSkeleton } from "./AboutPointPage.skeleton";
import { StyledAPContainer } from "./Styled";

const AboutPoint = () => {
    const { markets } = useAppSelector((s) => s.work);
    const { offlineMode } = useAppSelector((s) => s.offline);
    const { role } = useAppSelector((s) => s.user);
    const { currentUser, routeList } = useAppSelector((state) => state.sv);

    const { marketId } = useParams();

    const [market, setMarket] = useState<Market | null>(null);
    const [loading, setLoading] = useState(true);

    const dispatch = useAppDispatch();

    useEffect(() => {
        const getMarket = async () => {
            if (!marketId) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // 1. Отримуємо market
                const marketFromStore = markets[marketId];
                let currentMarket: Market | null = null;

                if (marketFromStore) {
                    currentMarket = marketFromStore;
                } else if (!offlineMode) {
                    const { result } = await getMarketInfo({
                        marketId,
                        withContracts: false,
                    });
                    currentMarket = result;
                }
                dispatch(
                    getPlanGetByMarketIdThunk({
                        marketId,
                        targetDate: dayjs().format(backendDateFormat.date),
                    }),
                );
                dispatch(
                    getKpiGetMarketProgressThunk({
                        marketId,
                        targetDate: dayjs().format(backendDateFormat.date),
                    }),
                );

                if (role === UserRole.Sv) {
                    const userId = currentUser.split("/").at(-1);
                    const userRoute = routeList?.[userId]?.uuid;

                    if (userRoute) {
                        dispatch(
                            getFullUnassignedPlanByRouteIdThunk({
                                routeId: userRoute,
                                targetDate: dayjs().format(
                                    backendDateFormat.date,
                                ),
                            }),
                        );
                    }
                }

                setMarket(currentMarket);
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        getMarket();
    }, [
        currentUser,
        dispatch,
        marketId,
        markets,
        offlineMode,
        role,
        routeList,
    ]);

    const content = useMemo(() => {
        if (loading) {
            return <AboutPointPageSkeleton />;
        }

        if (!market) {
            return (
                <StyledAPContainer>
                    <PrimaryText
                        textAlign="center"
                        fontSize={16}
                        style={{ margin: "auto" }}
                    >
                        Інформація відсутня
                    </PrimaryText>
                </StyledAPContainer>
            );
        }

        return <AboutPointPage market={market} />;
    }, [loading, market]);

    return (
        <>
            <Meta title="TT Info" />
            <Header title="Про ТТ" back titleFontSize={16} />

            {content}
        </>
    );
};

export default AboutPoint;
